import type { FC } from "react";

import { useLoyaltyApi } from "@ui/hooks/useLoyaltyApi";
import { usePagination } from "@ui/hooks/usePagination/usePagination";
import { useTranslations } from "@ui/providers/useTranslations";
import { getImageSource } from "@ui/utils/getImageSource/getImageSource";

import { useIsLoggedIn } from "@jti/app-context/PageAuthContext";
import { Loader } from "@jti/core-components/components/Loader";
import type { ArticleAEMData, BlogItemImage } from "@jti/fe-common";
import { useBlogListingLoyaltyLogic } from "@jti/rest-api";

import { ComponentHeading } from "../ComponentHeading";
import { LoadMore } from "../LoadMore";
import { ResponsiveContainer } from "../ResponsiveContainer";
import { Typography } from "../Typography";

import { BlogItem } from "./BlogItem/BlogItem";

import style from "./BlogListing.module.scss";

export type BlogListingProps = {
  isInEditor?: boolean;
  title: string;
  errorMessage: string;
  disableTiers?: boolean;
  articles: ArticleAEMData[];
  path: string;
  reversed?: boolean;
  disableLoyalty?: boolean;
};

export const BlogListing: FC<BlogListingProps> = ({
  articles,
  title,
  isInEditor,
  disableTiers,
  errorMessage,
  disableLoyalty,
  reversed,
}) => {
  const [paginatedArticles, loadMoreArticles] = usePagination(articles, 6, 7);
  const { __ } = useTranslations();
  const [featuredArticle, ...restArticles] = paginatedArticles;
  const loyaltyApi = useLoyaltyApi();
  const isRouteLoggedIn = useIsLoggedIn();
  const { hasError, isLoading, isLogged, articlesMetaData, featuredMetaData } = useBlogListingLoyaltyLogic(
    isInEditor,
    disableLoyalty,
    disableTiers,
    articles,
    featuredArticle,
    loyaltyApi
  );
  const isLoggedIn = isLogged || isRouteLoggedIn;

  if (hasError) {
    return (
      <Typography variant="h1" className={style.error}>
        {errorMessage}
      </Typography>
    );
  }

  const featuredBlogItemImage: BlogItemImage | null = getImageSource(featuredArticle?.image, title);

  return (
    <ResponsiveContainer size="large">
      <div className={style.container}>
        <div className={style.content} data-testid="articlesList">
          <ComponentHeading testId="articlesListHeading">{title}</ComponentHeading>

          {isLoading ? (
            <div className={style.loading}>
              <Loader />
            </div>
          ) : (
            <div className={style.innerWrapper}>
              <BlogItem
                key={`${featuredArticle?.path}_${featuredArticle?.articleId}`}
                {...featuredArticle}
                image={featuredBlogItemImage}
                isFeatured={true}
                reversed={reversed}
                tier={disableTiers ? undefined : featuredMetaData?.levels[0]}
                isAvailable={featuredMetaData?.is_available}
                points={featuredMetaData?.value}
                isRead={featuredMetaData?.is_read}
                isWatched={featuredMetaData?.is_watched}
                isCompleted={featuredMetaData?.is_completed}
                isLogged={isLoggedIn}
              />

              <div className={style.articles} data-testid="articleItems">
                {restArticles.map((article, articleIndex) => {
                  const metaData = disableLoyalty
                    ? null
                    : articlesMetaData.find((meta) => meta.article_id === article?.articleId);
                  const blogItemImage: BlogItemImage | null = getImageSource(article?.image, title);

                  return (
                    <BlogItem
                      key={`${article?.path}_${articleIndex}`}
                      {...article}
                      image={blogItemImage}
                      isFeatured={false}
                      reversed={reversed}
                      tier={disableTiers ? undefined : metaData?.levels[0]}
                      isAvailable={metaData?.is_available}
                      points={metaData?.value}
                      isRead={metaData?.is_read}
                      isWatched={metaData?.is_watched}
                      isCompleted={metaData?.is_completed}
                      isLogged={isLoggedIn}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
        {paginatedArticles < articles && <LoadMore label={__("load.more")} onClick={loadMoreArticles} />}
      </div>
    </ResponsiveContainer>
  );
};
